import { message, Tooltip } from 'antd';
import React, { useState, useRef, useEffect } from 'react';
import {
  InfoCircleOutlined,
  PlusOutlined,
  DeleteOutlined,
  DownOutlined,
} from '@ant-design/icons';
import colors from '../utils/theme/colors';
import UploadButton from './UploadButton';

// Custom beautiful input components
const CustomInput = ({ value, onChange, placeholder, multiline = false, rows = 3, type = 'text', step, className = '', autoExpand = false, onClean, ...props }) => {
  const [focused, setFocused] = useState(false);
  const [isMultilineMode, setIsMultilineMode] = useState(multiline);
  const inputRef = useRef(null);
  
  const handleKeyDown = (e) => {
    // Enable multi-line mode on Enter for regular inputs
    if (e.key === 'Enter' && !multiline) {
      e.preventDefault();
      const newValue = (value || '') + '\n';
      onChange(newValue);
      setIsMultilineMode(true);
    }
  };

  // Auto-expand textarea based on content
  useEffect(() => {
    if ((isMultilineMode || multiline) && autoExpand && inputRef.current) {
      inputRef.current.style.height = 'auto';
      inputRef.current.style.height = inputRef.current.scrollHeight + 'px';
    }
  }, [value, isMultilineMode, multiline, autoExpand]);

  // Refocus and restore cursor position after component change
  useEffect(() => {
    if (isMultilineMode && inputRef.current && !multiline) {
      inputRef.current.focus();
      // Set cursor to end
      const length = inputRef.current.value.length;
      inputRef.current.setSelectionRange(length, length);
    }
  }, [isMultilineMode, multiline]);

  const baseStyle = {
    width: '100%',
    padding: isMultilineMode || multiline ? '14px 16px' : '12px 16px',
    background: focused ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.06)',
    border: focused 
      ? '1px solid rgba(99, 102, 241, 0.8)' 
      : '1px solid rgba(255,255,255,0.15)',
    borderRadius: '12px',
    color: colors.foreground,
    fontSize: '14px',
    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
    outline: 'none',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    resize: autoExpand ? 'none' : (isMultilineMode || multiline) ? 'vertical' : 'none',
    minHeight: autoExpand ? '44px' : (isMultilineMode || multiline) ? `${rows * 20 + 28}px` : '44px',
    maxHeight: autoExpand ? '400px' : 'none',
    overflowY: autoExpand ? 'auto' : 'visible',
    boxSizing: 'border-box',
    boxShadow: focused 
      ? '0 0 0 3px rgba(99, 102, 241, 0.1), 0 2px 8px rgba(0,0,0,0.15)' 
      : '0 1px 3px rgba(0,0,0,0.1)',
    backdropFilter: 'blur(8px)',
    WebkitBackdropFilter: 'blur(8px)',
  };

  const Element = (isMultilineMode || multiline) ? 'textarea' : 'input';
  
  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <Element
        ref={inputRef}
        type={type}
        step={step}
        value={value === null || value === undefined ? '' : value}
        onChange={(e) => onChange(e.target.value)}
        onKeyDown={handleKeyDown}
        onFocus={() => setFocused(true)}
        onBlur={() => {
          setFocused(false);
          // Clean text on blur if onClean callback provided
          if (onClean && value) {
            const cleaned = value?.replace(/\s+/g, ' ').trim() || '';
            if (cleaned !== value) {
              onChange(cleaned);
            }
          }
        }}
        placeholder={placeholder}
        style={baseStyle}
        className={className}
        {...props}
      />
    </div>
  );
};

const CustomSwitch = ({ checked, onChange }) => {
  const [focused, setFocused] = useState(false);
  
  return (
    <div
      onClick={() => onChange(!checked)}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
      tabIndex={0}
      style={{
        width: '52px',
        height: '28px',
        borderRadius: '14px',
        background: checked 
          ? 'linear-gradient(135deg, #6366f1, #8b5cf6)' 
          : 'rgba(255,255,255,0.15)',
        border: focused 
          ? '1px solid rgba(99, 102, 241, 0.8)' 
          : '1px solid rgba(255,255,255,0.2)',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        padding: '3px',
        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
        outline: 'none',
        boxShadow: focused 
          ? '0 0 0 3px rgba(99, 102, 241, 0.1), 0 2px 8px rgba(0,0,0,0.15)' 
          : '0 2px 4px rgba(0,0,0,0.1)',
        backdropFilter: 'blur(8px)',
        WebkitBackdropFilter: 'blur(8px)',
      }}
    >
      <div
        style={{
          width: '20px',
          height: '20px',
          borderRadius: '50%',
          background: 'linear-gradient(135deg, #ffffff, #f8fafc)',
          transform: checked ? 'translateX(24px)' : 'translateX(0)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          boxShadow: '0 2px 6px rgba(0,0,0,0.2), inset 0 1px 0 rgba(255,255,255,0.3)',
          border: '1px solid rgba(0,0,0,0.1)',
        }}
      />
    </div>
  );
};

const CustomSelect = ({ value, onChange, options, placeholder }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [focused, setFocused] = useState(false);
  
  return (
    <div style={{ position: 'relative', width: '100%' }}>
      <div
        onClick={() => setIsOpen(!isOpen)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        tabIndex={0}
        style={{
          width: '100%',
          padding: '12px 16px',
          background: focused || isOpen ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.06)',
          border: focused || isOpen 
            ? '1px solid rgba(99, 102, 241, 0.8)' 
            : '1px solid rgba(255,255,255,0.15)',
          borderRadius: '12px',
          color: colors.foreground,
          fontSize: '14px',
          fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          minHeight: '44px',
          boxSizing: 'border-box',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          outline: 'none',
          boxShadow: focused || isOpen
            ? '0 0 0 3px rgba(99, 102, 241, 0.1), 0 2px 8px rgba(0,0,0,0.15)' 
            : '0 1px 3px rgba(0,0,0,0.1)',
          backdropFilter: 'blur(8px)',
          WebkitBackdropFilter: 'blur(8px)',
        }}
      >
        <span style={{ 
          color: value ? colors.foreground : colors.muted_foreground,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {value || placeholder}
        </span>
        <DownOutlined style={{ 
          fontSize: '12px', 
          color: colors.muted_foreground,
          transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          marginLeft: '8px',
          flexShrink: 0,
        }} />
      </div>
      
      {isOpen && (
        <div
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            background: 'rgba(15, 15, 15, 0.95)',
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: '12px',
            marginTop: '8px',
            zIndex: 1000,
            maxHeight: '240px',
            overflowY: 'auto',
            boxShadow: '0 12px 40px rgba(0,0,0,0.4), 0 4px 12px rgba(0,0,0,0.2)',
            animation: 'slideDown 0.2s ease-out',
          }}
        >
          {options.map((option, index) => (
            <div
              key={option.value}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
              style={{
                padding: '14px 16px',
                color: value === option.value ? '#6366f1' : colors.foreground,
                fontSize: '14px',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                cursor: 'pointer',
                borderBottom: index < options.length - 1 ? '1px solid rgba(255,255,255,0.08)' : 'none',
                transition: 'all 0.2s ease',
                fontWeight: value === option.value ? '500' : '400',
              }}
              onMouseEnter={(e) => {
                e.target.style.background = 'rgba(99, 102, 241, 0.1)';
                e.target.style.color = '#6366f1';
              }}
              onMouseLeave={(e) => {
                e.target.style.background = 'transparent';
                e.target.style.color = value === option.value ? '#6366f1' : colors.foreground;
              }}
            >
              {option.label}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

function InputParameter({ param, value, onChange = () => {} }) {
  const [tempInput, setTempInput] = useState('');

  // Helper to check if a given string looks like an image URL
  const isImageUrl = (url) =>
    typeof url === 'string' &&
    url.trim() !== '' &&
    /(https?:\/\/.*\.(?:png|jpe?g|gif|webp|bmp|svg))/i.test(url);

  // For object[] type, manage array logic
  const addArrayItem = () => {
    if (param.data_type === 'object[]') {
      const currentValue = Array.isArray(value) ? value : [];
      onChange([...currentValue, '']);
    }
  };

  const removeArrayItem = (index) => {
    if (param.data_type === 'object[]') {
      const currentValue = Array.isArray(value) ? [...value] : [];
      currentValue.splice(index, 1);
      onChange(currentValue);
    }
  };

  const updateArrayItem = (index, newValue) => {
    if (param.data_type === 'object[]') {
      const currentValue = Array.isArray(value) ? [...value] : [];
      currentValue[index] = newValue;
      onChange(currentValue);
    }
  };

  // Render input based on parameter type
  const renderInput = () => {
    // Special handling for prompt parameters - large textarea
    if (param.parameter_name === 'prompt' || param.parameter_name === 'negative_prompt') {
      return (
        <CustomInput
          value={Array.isArray(value) ? value.join('\n') : value || ''}
          onChange={onChange}
          placeholder={param.parameter_name === 'prompt' ? 'Describe what you want to create...' : `Enter ${param.parameter_name}`}
          multiline={true}
          autoExpand={true}
          onClean={true}
          rows={param.parameter_name === 'prompt' ? 6 : 4}
        />
      );
    }

    // Handle array-like parameter (object[])
    // Handle image array parameter (object[] of image URLs)
    if (param.data_type === 'object[]' && 
        (param.parameter_name.toLowerCase().includes('image_urls') || 
         param.parameter_name.toLowerCase().includes('image_url'))) {
        
        const imageUrls = Array.isArray(value) ? value : [];
        
        const addImageUrl = (newUrl) => {
          if (newUrl && newUrl.trim()) {
            const currentUrls = Array.isArray(value) ? value : [];
            const newUrls = [...currentUrls, newUrl.trim()];
            console.log('Before onChange - Current URLs:', currentUrls, 'Adding:', newUrl.trim(), 'New array:', newUrls);
            onChange(newUrls);
          }
        };
        
        const removeImageUrl = (index) => {
          const newUrls = [...imageUrls];
          newUrls.splice(index, 1);
          onChange(newUrls);
        };
        
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {/* Input for adding new image URL */}
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              <div style={{ flex: 1 }}>
                <CustomInput
                  value={tempInput}
                  onChange={setTempInput}
                  placeholder="Enter image URL"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && tempInput.trim()) {
                      addImageUrl(tempInput.trim());
                      setTempInput('');
                    }
                  }}
                />
              </div>
              <button
                onClick={() => {
                  if (tempInput.trim()) {
                    addImageUrl(tempInput.trim());
                    setTempInput('');
                  }
                }}
                disabled={!tempInput.trim()}
                style={{
                  padding: '10px 12px',
                  borderRadius: '6px',
                  border: '1px solid rgba(99, 102, 241, 0.3)',
                  background: tempInput.trim() ? 'rgba(99, 102, 241, 0.1)' : 'rgba(255,255,255,0.05)',
                  color: tempInput.trim() ? '#6366f1' : '#666',
                  fontSize: '12px',
                  fontWeight: '500',
                  cursor: tempInput.trim() ? 'pointer' : 'not-allowed',
                  transition: 'all 0.2s ease',
                  minWidth: '45px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                Add
              </button>
              <UploadButton
                onUpload={(uploadedImageUrl) => {
                  addImageUrl(uploadedImageUrl);
                  message.success('Image uploaded successfully!');
                }}
                maxSizeKB={5120}
              />
            </div>
            
            {/* Display existing images */}
            {imageUrls.filter(url => url && url.trim()).length > 0 && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                <span style={{ fontSize: '12px', color: '#666', fontWeight: 500 }}>
                  {imageUrls.filter(url => url && url.trim()).length} image{imageUrls.filter(url => url && url.trim()).length !== 1 ? 's' : ''}:
                </span>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                  {imageUrls.filter(url => url && url.trim()).map((url, index) => {
                    const originalIndex = imageUrls.indexOf(url);
                    return (
                      <div key={index} style={{ 
                      position: 'relative',
                      width: '60px', 
                      height: '60px', 
                      borderRadius: '8px', 
                      overflow: 'hidden',
                      border: '1px solid #ddd'
                    }}>
                      {isImageUrl(url) ? (
                        <img
                          src={url}
                          alt={`Preview ${index + 1}`}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                          }}
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }}
                        />
                      ) : (
                        <div style={{
                          width: '100%',
                          height: '100%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          backgroundColor: '#f5f5f5',
                          fontSize: '12px',
                          color: '#666'
                        }}>
                          IMG
                        </div>
                      )}
                      <button
                        onClick={() => removeImageUrl(originalIndex)}
                        style={{
                          position: 'absolute',
                          top: '2px',
                          right: '2px',
                          width: '16px',
                          height: '16px',
                          borderRadius: '50%',
                          border: 'none',
                          backgroundColor: 'rgba(255, 0, 0, 0.8)',
                          color: 'white',
                          fontSize: '10px',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        ×
                      </button>
                    </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        );
      }
  
    if (param.data_type === 'object[]') {
      return (
        <div>
          {Array.isArray(value) &&
            value.map((item, index) => (
              <div key={index} style={{ display: 'flex', gap: '8px', marginBottom: '8px' }}>
                <div style={{ flex: 1 }}>
                  <CustomInput
                    value={item}
                    onChange={(newValue) => updateArrayItem(index, newValue)}
                    placeholder={`Item ${index + 1}`}
                  />
                </div>
                {Array.isArray(value) && value.length > 1 && (
                  <div
                    onClick={() => removeArrayItem(index)}
                    style={{
                      width: '44px',
                      height: '44px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      color: colors.destructive,
                      borderRadius: '8px',
                      border: '1px solid rgba(239, 68, 68, 0.2)',
                      background: 'rgba(239, 68, 68, 0.1)',
                      transition: 'all 0.2s ease',
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = 'rgba(239, 68, 68, 0.2)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = 'rgba(239, 68, 68, 0.1)';
                    }}
                  >
                    <DeleteOutlined />
                  </div>
                )}
              </div>
            ))}
          <div style={{ marginTop: '8px' }}>
            <div
              onClick={addArrayItem}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                cursor: 'pointer',
                color: colors.muted_foreground,
                fontSize: '14px',
                padding: '12px',
                borderRadius: '8px',
                border: '1px dashed rgba(255,255,255,0.2)',
                background: 'rgba(255,255,255,0.02)',
                transition: 'all 0.2s ease',
                minHeight: '44px',
              }}
              onMouseEnter={(e) => {
                e.target.style.color = colors.foreground;
                e.target.style.borderColor = 'rgba(99, 102, 241, 0.4)';
                e.target.style.background = 'rgba(99, 102, 241, 0.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.color = colors.muted_foreground;
                e.target.style.borderColor = 'rgba(255,255,255,0.2)';
                e.target.style.background = 'rgba(255,255,255,0.02)';
              }}
            >
              <PlusOutlined />
              Add Item
            </div>
          </div>
        </div>
      );
    }

    // Handle dropdown-like parameter (with allowed_values)
    if (param.allowed_values) {
      const options = Array.isArray(param.allowed_values) 
        ? param.allowed_values.map(val => ({ value: val, label: val }))
        : param.allowed_values.split(',').map(val => ({ value: val.trim(), label: val.trim() }));
        
      return (
        <CustomSelect
          value={value}
          onChange={onChange}
          placeholder={`Select ${param.parameter_name}`}
          options={options}
        />
      );
    }

    // Handle boolean parameter
    if (param.data_type === 'boolean') {
      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <CustomSwitch checked={value} onChange={onChange} />
          <span style={{ color: colors.muted_foreground, fontSize: '14px' }}>
            {value ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      );
    }

    // Handle number/integer/float parameters
    if (param.data_type === 'number' || param.data_type === 'integer' || param.data_type === 'float') {
      return (
        <CustomInput
          type="number"
          step={param.data_type === 'float' ? '0.1' : '1'}
          value={value}
          onChange={(newValue) => {
            const parsed = param.data_type === 'integer' ? parseInt(newValue) : parseFloat(newValue);
            onChange(isNaN(parsed) ? 0 : parsed);
          }}
          placeholder={`Enter ${param.parameter_name}`}
        />
      );
    }

    // Handle image parameter
    if (param.parameter_name === 'image' || param.parameter_name.toLowerCase().includes('image_url')) {
      return (
        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <CustomInput
              value={value}
              onChange={onChange}
              onClean={true}
              placeholder="Enter image URL or upload an image"
            />
          </div>
          <UploadButton
            onUpload={(uploadedImageUrl) => {
              onChange(uploadedImageUrl);
              message.success('Image uploaded successfully!');
            }}
            maxSizeKB={5120}
          />
          {isImageUrl(value) && (
            <div style={{ width: '44px', height: '44px', borderRadius: '8px', overflow: 'hidden', flexShrink: 0 }}>
              <img
                src={value}
                alt="Preview"
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                }}
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            </div>
          )}
        </div>
      );
    }


    // Handle object parameter
    if (param.data_type === 'object') {
      return (
        <CustomInput
          value={typeof value === 'object' ? JSON.stringify(value, null, 2) : value}
          onChange={(newValue) => {
            try {
              const parsed = JSON.parse(newValue);
              onChange(parsed);
            } catch (err) {
              onChange(newValue);
            }
          }}
          placeholder={`Enter JSON for ${param.parameter_name}`}
          multiline={true}
          autoExpand={true}
          rows={4}
        />
      );
    }

    // Default string parameter
    return (
      <CustomInput
        value={value}
        onChange={onChange}
        onClean={true}
        placeholder={`Enter ${param.parameter_name}`}
      />
    );
  };

  return (
    <div
      style={{
        width: '100%',
        padding: '8px 0 14px',
        background: 'transparent',
        boxSizing: 'border-box',
        overflow: 'visible',
        border: 'none',
      }}
    >
      <div style={{ marginBottom: 8 }}>
        <div style={{ width: '100%' }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ 
              fontSize: '14px', 
              fontWeight: 600,
              color: colors.foreground
            }}>
              {param.parameter_title || param.parameter_name}
              {(Number(param.is_required) === 1 || param.is_required === true) ? <span style={{ color: '#ff6b6b', marginLeft: 4 }}>*</span> : null}
            </span>
            {(param.description || param.help_text) && (
              <Tooltip title={param.description || param.help_text} placement="top">
                <InfoCircleOutlined
                  style={{
                    marginLeft: 8,
                    color: colors.muted_foreground,
                    cursor: 'help'
                  }}
                />
              </Tooltip>
            )}
          </div>
        </div>
      </div>
      {renderInput()}
    </div>
  );
}

export default InputParameter;
